import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'contexts/Localization';
import { DEFAULT_META, getCustomMeta } from 'config/constants/meta';
import { useXpBusdPrice } from 'hooks/useBUSDPrice';
import Container from './Container';

const StyledPage = styled(Container)`
  min-height: calc(100vh - 64px);
  padding-top: 16px;
  padding-bottom: 16px;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding-top: 24px;
    padding-bottom: 24px;
  }

  ${({ theme }) => theme.mediaQueries.lg} {
    padding-top: 32px;
    padding-bottom: 32px;
  }
`;

export const PageMeta: React.FC<{ symbol?: string }> = ({ symbol }) => {
  const { t } = useTranslation();
  const pathname = window.location.pathname;
  const xpPriceUsd = useXpBusdPrice();
  const xpPriceUsdDisplay = xpPriceUsd ? `$${xpPriceUsd.toFixed(3)}` : '...';

  const pageMeta = getCustomMeta(pathname, t) || {};
  // Now extracting keywords as well
  const { title, description, image, keywords } = { ...DEFAULT_META, ...pageMeta };
  let pageTitle = xpPriceUsdDisplay ? [title, xpPriceUsdDisplay].join(' - ') : title;
  if (symbol) {
    pageTitle = [symbol, title].join(' - ');
  }

  return (
    <Helmet>
      <title>{pageTitle}</title>
      {keywords && <meta name="keywords" content={keywords} />}
      
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  symbol?: string;
}

const Page: React.FC<PageProps> = ({ children, symbol, ...props }) => {
  return (
    <>
      <PageMeta symbol={symbol} />
      <StyledPage {...props}>{children}</StyledPage>
    </>
  );
};

export default Page;